/** Shared dot-style pagination for the carousels that render
 *  `snippets/carousel-pagination.liquid` (the translucent pill).
 *
 *  Builds one indicator per scroll "stop" (card-step paging) inside the given
 *  container, reflects the active page, and lets a click jump to that page.
 *  The active indicator renders as an elongated pill; the rest as dots.
 *
 *  Optional autoplay: when enabled, the pill auto-advances one page on a timer
 *  and the active dot fills white left-to-right in sync with the countdown
 *  (mirrors `showcase-slideshow`). Autoplay pauses on hover/focus of the
 *  `pauseOn` element, when the tab is hidden, when the pill isn't visible (e.g.
 *  a hidden explore-carousel rail), and when reduced motion is preferred.
 *
 *  Usage from a carousel custom element:
 *    this.#pagination = new CarouselPagination(containerEl, {
 *      signal,
 *      onSelect: (x) => this.#booster.scrollTo({ x }),
 *      autoplay: true,   // or a number of ms
 *      pauseOn: this,    // element that pauses autoplay on hover/focus
 *    })
 *    // inside the booster's onUpdate / resize handlers:
 *    this.#pagination.update({ current, max, step })
 */

const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const DEFAULT_AUTOPLAY_MS = 6000
const ACTIVE_CLASS = 'is-active'
const FILLING_CLASS = 'is-filling'
const PAUSED_CLASS = 'is-paused'

export class CarouselPagination {
  #container
  #onSelect
  #signal
  #dots = []
  #positions = []
  #active = -1
  #autoplayMs = 0
  #pauseOn = null
  #reduceMotionMql
  #timer
  #timerStart = 0
  #remaining = 0
  #paused = false

  constructor(container, { onSelect, signal, autoplay = false, pauseOn = null } = {}) {
    this.#container = container ?? null
    this.#onSelect = onSelect
    this.#signal = signal
    this.#autoplayMs = autoplay === true ? DEFAULT_AUTOPLAY_MS : Number(autoplay) || 0
    this.#pauseOn = pauseOn ?? null

    if (this.#autoplayMs > 0) {
      this.#remaining = this.#autoplayMs
      this.#reduceMotionMql = globalThis.matchMedia?.(REDUCE_MOTION_QUERY)
      this.#container?.style.setProperty('--carousel-autoplay-ms', `${this.#autoplayMs}ms`)

      const target = this.#pauseOn ?? this.#container
      if (target) {
        target.addEventListener('pointerenter', this.#pause, { signal })
        target.addEventListener('pointerleave', this.#resume, { signal })
        target.addEventListener('focusin', this.#pause, { signal })
        target.addEventListener('focusout', this.#resume, { signal })
      }
      document.addEventListener('visibilitychange', this.#onVisibility, { signal })
    }
  }

  /** Recompute pages from scroll metrics, rebuild indicators when the count
   *  changes, and highlight the page nearest the current scroll position. */
  update({ current = 0, max = 0, step = 0 } = {}) {
    if (!this.#container) return

    const pageCount = max > 1 && step > 0 ? Math.ceil(max / step) + 1 : 1
    this.#positions = Array.from({ length: pageCount }, (_, i) =>
      Math.min(i * step, max)
    )

    if (pageCount !== this.#dots.length) this.#build(pageCount)
    this.#setActive(this.#nearest(current))
  }

  destroy() {
    clearTimeout(this.#timer)
    this.#timer = undefined
    this.#container?.replaceChildren()
    this.#dots = []
    this.#positions = []
    this.#active = -1
  }

  #build(pageCount) {
    clearTimeout(this.#timer)
    this.#timer = undefined
    this.#container.replaceChildren()
    this.#dots = []
    this.#active = -1
    if (pageCount <= 1) return

    for (let i = 0; i < pageCount; i += 1) {
      const dot = document.createElement('button')
      dot.type = 'button'
      dot.className = 'dot'
      dot.setAttribute('aria-label', `Go to group ${i + 1} of ${pageCount}`)
      dot.addEventListener('click', () => this.#onSelect?.(this.#positions[i]), {
        signal: this.#signal,
      })
      this.#container.append(dot)
      this.#dots.push(dot)
    }
  }

  #nearest(current) {
    let active = 0
    let closest = Infinity
    for (let i = 0; i < this.#positions.length; i += 1) {
      const distance = Math.abs(current - this.#positions[i])
      if (distance < closest) {
        closest = distance
        active = i
      }
    }
    return active
  }

  #setActive(index) {
    if (index === this.#active || this.#dots.length === 0) return
    this.#active = index
    this.#dots.forEach((dot, i) => {
      const isActive = i === index
      dot.classList.toggle(ACTIVE_CLASS, isActive)
      if (isActive) {
        dot.setAttribute('aria-current', 'true')
      } else {
        dot.removeAttribute('aria-current')
      }
    })

    // Any change of active page (autoplay, drag, arrow, click) restarts the
    // countdown + fill for the newly active dot.
    if (this.#autoplayMs > 0) this.#startTimer()
  }

  get #canAutoplay() {
    return (
      this.#autoplayMs > 0 &&
      this.#dots.length > 1 &&
      !this.#reduceMotionMql?.matches
    )
  }

  get #isVisible() {
    return Boolean(this.#container && this.#container.offsetParent !== null)
  }

  #startTimer() {
    clearTimeout(this.#timer)
    this.#timer = undefined
    this.#remaining = this.#autoplayMs
    this.#container?.classList.remove(PAUSED_CLASS)
    if (!this.#canAutoplay || this.#paused) {
      this.#clearFill()
      return
    }

    this.#timerStart = Date.now()
    this.#timer = setTimeout(this.#onTick, this.#remaining)
    this.#restartFill()
  }

  #onTick = () => {
    this.#timer = undefined
    if (!this.#canAutoplay || this.#paused) return
    if (!this.#isVisible) {
      // e.g. a hidden explore-carousel rail — wait a cycle without advancing.
      this.#startTimer()
      return
    }
    this.#advance()
  }

  #advance() {
    const count = this.#dots.length
    if (count <= 1) return

    let next = this.#active + 1
    // Wrap to the start when the next page wouldn't move forward (also covers
    // duplicate end pages produced by the ceil()-based page count).
    if (next >= count || this.#positions[next] <= this.#positions[this.#active] + 1) {
      next = 0
    }
    this.#onSelect?.(this.#positions[next])
  }

  #pause = () => {
    if (this.#paused) return
    this.#paused = true
    this.#container?.classList.add(PAUSED_CLASS)
    if (this.#timer === undefined) return
    clearTimeout(this.#timer)
    this.#timer = undefined
    this.#remaining = Math.max(
      0,
      this.#remaining - (Date.now() - this.#timerStart)
    )
  }

  #resume = () => {
    if (!this.#paused) return
    this.#paused = false
    this.#container?.classList.remove(PAUSED_CLASS)
    if (!this.#canAutoplay) return

    const active = this.#dots[this.#active]
    if (active && !active.classList.contains(FILLING_CLASS)) this.#restartFill()

    this.#timerStart = Date.now()
    this.#timer = setTimeout(this.#onTick, this.#remaining)
  }

  #onVisibility = () => {
    if (document.hidden) this.#pause()
    else this.#resume()
  }

  #clearFill() {
    this.#dots.forEach((dot) => dot.classList.remove(FILLING_CLASS))
  }

  #restartFill() {
    this.#clearFill()
    const active = this.#dots[this.#active]
    if (!active) return
    // Force reflow so re-adding the class replays the fill animation from 0.
    active.getBoundingClientRect()
    active.classList.add(FILLING_CLASS)
  }
}
